<template>
    <!-- Mobile Layout -->
    <div
        class="md:hidden flex items-start gap-4 p-4 h-full bg-white dark:bg-slate-800 rounded-lg shadow-[0_1px_2px_0_rgba(0,0,0,0.05)] dark:shadow-slate-900/50 cursor-pointer active:bg-slate-50 dark:active:bg-slate-700 transition-colors duration-150"
        role="button"
        :aria-label="`View best card for ${category.category_name}`"
        @click="$emit('click', category)"
        tabindex="0"
        @keydown.enter="$emit('click', category)"
        @keydown.space.prevent="$emit('click', category)"
    >
        <!-- Card Image -->
        <div
            class="w-[76px] h-12 shrink-0 rounded flex items-center justify-center overflow-hidden bg-slate-100 dark:bg-slate-700"
        >
            <img
                v-if="cardImageUrl"
                :src="cardImageUrl"
                :alt="category.best_card.card_title"
                class="max-w-full max-h-full object-contain"
            />
            <div v-else class="w-full h-full flex items-center justify-center">
                <Icon icon="credit-card" class="w-6 h-6 text-slate-300 dark:text-slate-500" />
            </div>
        </div>

        <!-- Card Info -->
        <div class="flex flex-col items-start flex-1">
            <!-- Category with icon -->
            <div class="flex items-start gap-2 pb-2">
                <Icon
                    :icon="category.category_icon"
                    class="w-5 h-5 text-slate-500 dark:text-slate-400 shrink-0"
                />
                <span class="text-slate-500 dark:text-slate-400 text-sm font-medium leading-5">
                    {{ category.category_name }}
                </span>
            </div>
            <!-- Card Title -->
            <p class="self-stretch text-sm font-normal leading-5">
                <span class="text-slate-800 dark:text-slate-100">
                    {{ category.best_card.card_title }}
                </span>
                <span
                    v-if="category.best_card.last_four"
                    class="text-slate-500 dark:text-slate-400"
                >
                    ({{ category.best_card.last_four }})
                </span>
            </p>
            <!-- Points -->
            <p class="text-orange text-base font-medium leading-6">
                {{ category.best_card.multiplier_display }}
            </p>
        </div>
    </div>

    <!-- Desktop Layout -->
    <div
        class="hidden md:flex md:flex-col group bg-white dark:bg-slate-800 rounded-xl border border-transparent hover:border-slate-400 dark:hover:border-slate-500 active:border-slate-400 dark:active:border-slate-500 focus:border-slate-400 dark:focus:border-slate-500 focus:outline-none hover:shadow-md dark:hover:shadow-slate-900/50 transition-all cursor-pointer py-3 px-4 h-full"
        role="button"
        :aria-label="`View best card for ${category.category_name}`"
        @click="$emit('click', category)"
        tabindex="0"
        @keydown.enter="$emit('click', category)"
        @keydown.space.prevent="$emit('click', category)"
    >
        <!-- Category Header -->
        <div class="pb-3 flex items-center gap-2">
            <Icon
                :icon="category.category_icon"
                class="w-5 h-5 text-slate-500 dark:text-slate-400"
            />
            <span class="text-sm font-medium text-slate-500 dark:text-slate-400 leading-5">
                {{ category.category_name }}
            </span>
        </div>

        <!-- Card Image -->
        <div class="relative mb-4 mx-auto w-full max-w-[320px]">
            <div
                class="aspect-[1.586/1] rounded-lg overflow-hidden bg-slate-100 dark:bg-slate-700"
            >
                <img
                    v-if="cardImageUrl"
                    :src="cardImageUrl"
                    :alt="category.best_card.card_title"
                    class="w-full h-full object-cover"
                />
                <div
                    v-else
                    class="w-full h-full flex items-center justify-center"
                >
                    <Icon icon="credit-card" class="w-12 h-12 text-slate-300 dark:text-slate-500" />
                </div>
            </div>
        </div>

        <!-- Card Details -->
        <div class="flex flex-col grow">
            <!-- Card Title -->
            <div class="mb-2">
                <h3 class="text-sm font-medium leading-tight text-slate-800 dark:text-slate-100">
                    {{ category.best_card.card_title }}
                    <span
                        v-if="category.best_card.last_four"
                        class="text-slate-400 dark:text-slate-500 font-normal whitespace-nowrap"
                    >
                        ({{ category.best_card.last_four }})
                    </span>
                </h3>
            </div>

            <!-- Reward Display with Popover (always at bottom) -->
            <div class="flex items-center justify-between mt-auto">
                <span class="text-orange font-semibold text-sm">
                    {{ category.best_card.multiplier_display }}
                </span>
                <div class="relative">
                    <button
                        type="button"
                        @mousedown.stop.prevent="togglePopover"
                        @click.stop.prevent
                        @keydown.enter.stop.prevent="togglePopover"
                        class="text-slate-400 dark:text-slate-500 hover:text-slate-600 dark:hover:text-slate-300 transition-colors rounded p-1"
                        aria-label="Show card details"
                    >
                        <Icon
                            icon="chevron-right"
                            class="w-5 h-5 text-slate-400 dark:text-slate-500 group-hover:text-slate-800 dark:group-hover:text-slate-100 transition-all duration-200"
                            :class="{ '-rotate-90': showPopover }"
                        />
                    </button>
                    <!-- Popover -->
                    <Transition name="fade">
                        <div
                            v-if="showPopover"
                            v-click-outside="() => (showPopover = false)"
                            class="absolute bottom-full right-0 mb-2 w-64 bg-white dark:bg-slate-800 rounded-lg border border-slate-200 dark:border-slate-700 shadow-lg dark:shadow-slate-900/50 p-3 z-30"
                            @mousedown.stop
                            @click.stop
                        >
                            <div class="space-y-2">
                                <div
                                    v-if="category.best_card.program_name"
                                    class="flex justify-between text-xs"
                                >
                                    <span class="text-slate-500 dark:text-slate-400">Program</span>
                                    <span class="text-slate-700 dark:text-slate-200">
                                        {{ category.best_card.program_name }}
                                    </span>
                                </div>
                                <div
                                    v-if="
                                        !category.best_card.is_cashback &&
                                        category.best_card.program_valuation
                                    "
                                    class="flex justify-between text-xs"
                                >
                                    <span class="text-slate-500 dark:text-slate-400">
                                        Point Value
                                    </span>
                                    <span class="text-slate-700 dark:text-slate-200">
                                        {{
                                            category.best_card
                                                .program_valuation
                                        }}¢
                                    </span>
                                </div>
                                <div class="flex justify-between text-xs">
                                    <span class="text-slate-500 dark:text-slate-400">
                                        Effective Value
                                    </span>
                                    <span class="text-slate-700 dark:text-slate-200 font-medium">
                                        {{
                                            category.best_card.effective_value
                                        }}¢ per $1
                                    </span>
                                </div>
                                <div
                                    v-if="category.best_card.issuing_bank"
                                    class="flex justify-between text-xs"
                                >
                                    <span class="text-slate-500 dark:text-slate-400">Bank</span>
                                    <span class="text-slate-700 dark:text-slate-200">
                                        {{ category.best_card.issuing_bank }}
                                    </span>
                                </div>
                            </div>
                        </div>
                    </Transition>
                </div>
            </div>
        </div>
    </div>
</template>

<script setup>
import { ref, computed } from 'vue';
import Icon from '@/components/ui/icon/icon.vue';

const props = defineProps({
    category: {
        type: Object,
        required: true,
    },
});

defineEmits(['click']);

const cdnUrl = computed(() => window.cdnUrl);

const cardImageUrl = computed(() => {
    const img = props.category.best_card?.card_image;
    if (img?.path && img?.file_name) {
        return `${cdnUrl.value}/${img.path}/_small/${img.file_name}`;
    }
    return null;
});

const showPopover = ref(false);

const togglePopover = () => {
    showPopover.value = !showPopover.value;
};
</script>

<style scoped>
.fade-enter-active,
.fade-leave-active {
    transition: opacity 0.15s ease;
}

.fade-enter-from,
.fade-leave-to {
    opacity: 0;
}
</style>
